import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

/** One built-in template as the picker shows it, composed server-side. */
export interface ThankYouTemplateView {
  id: string
  title: string
  /** The fully-composed preview — tokens filled from the hunt, signed off. */
  previewText: string
}

export interface ThankYouInfo {
  /** ISO timestamp of the last send, or null if never — drives "Not sent yet". */
  sentAt: string | null
  participantCount: number
  participantNames: string[]
  templates: ThankYouTemplateView[]
  /**
   * Whether this is an estate hunt. The thank-you is an estate-team feature, so the
   * card is offered only when this is true — the client gets the answer, never the
   * team's raw marker.
   */
  isEstate: boolean
}

/** Exactly one of templateId / customBody is set. */
export interface SendThankYouRequest {
  templateId?: string
  customBody?: string
  locale?: string
  /** True to send again after one was already sent for this hunt. */
  confirm?: boolean
}

export type ThankYouRecipientStatus = 'sent' | 'no_email' | 'failed'

export interface ThankYouRecipientResult {
  userId: string
  userName: string
  status: ThankYouRecipientStatus
  reason?: string | null
}

export interface SendThankYouResult {
  emailSent: number
  total: number
  sentAt: string
  results: ThankYouRecipientResult[]
}

function base(groupId: string | undefined, huntId: string | undefined): string {
  return `/clubhouse/groups/${encodeURIComponent(groupId ?? '')}/hunts/${encodeURIComponent(huntId ?? '')}/thank-you`
}

/**
 * The thank-you card + picker data for an ended hunt: whether one was already
 * sent, who would receive it, and the localised template previews. The server
 * enforces owner/admin/leader and returns 403 to anyone else, so a non-manager's
 * query fails and the card is never shown. `locale` is the browser's language —
 * the built-in templates are composed in it.
 */
export function useThankYouInfo(
  groupId: string | undefined, huntId: string | undefined, locale: string, enabled = true,
) {
  const call = useApiFetch()
  return useQuery({
    queryKey: ['clubhouse', 'hunts', groupId, huntId, 'thank-you', locale],
    queryFn: () => call<ThankYouInfo>(`${base(groupId, huntId)}?locale=${encodeURIComponent(locale)}`),
    enabled: enabled && !!groupId && !!huntId,
    // A first paint may be stale, but the "already sent" state must be fresh so the
    // resend confirm is shown when it should be — revalidate on every open.
    staleTime: 0,
  })
}

/**
 * Send the thank-you. The response is the honest per-recipient breakdown; a 409
 * `thank_you.already_sent` (resend without confirm) and a 503
 * `thank_you.delivery_not_configured` surface as ApiErrors the caller branches on.
 */
export function useSendThankYou(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: SendThankYouRequest) =>
      call<SendThankYouResult>(base(groupId, huntId), { method: 'POST', body: JSON.stringify(request) }),
    onSuccess: () => {
      // Refresh the card so it flips to "Sent" (and the resend confirm arms).
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'hunts', groupId, huntId, 'thank-you'] })
    },
    onError: (e) => {
      console.error('Failed to send thank-you', e)
    },
  })
}
